var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2021, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef } from 'react';
import { css } from '@emotion/react';
import Body from '../Body';
import Headline from '../Headline';
import ButtonGroup from '../ButtonGroup';
import { spacing, cx } from '../../styles/style-mixins';
import Image from '../Image';
import { isString } from '../../util/type-check';
const wrapperStyles = css `
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const imageStyles = css `
  height: 160px;
  max-width: 280px;
  object-fit: contain;
`;
const svgStyles = css `
  height: 100%;
  width: 100%;
`;
function NotificationImage({ image, }) {
    if ('svg' in image) {
        const Svg = image.svg;
        const isDecorative = !image.alt;
        return (_jsx("div", Object.assign({ css: imageStyles }, { children: _jsx(Svg, Object.assign({ css: svgStyles }, (isDecorative
                ? { 'aria-hidden': true }
                : { 'aria-label': image.alt, 'role': 'img' }))) })));
    }
    return _jsx(Image, Object.assign({}, image, { css: imageStyles }));
}
const centeredStyles = css `
  text-align: center;
`;
/**
 * The `NotificationFullscreen` component provides important information or
 * feedback as part of a process flow.
 */
export const NotificationFullscreen = forwardRef((_a, ref) => {
    var { image, headline, body, actions } = _a, props = __rest(_a, ["image", "headline", "body", "actions"]);
    const headlineLabel = isString(headline) ? headline : headline.label;
    const headlineElement = isString(headline) ? 'h2' : headline.as;
    return (_jsxs("div", Object.assign({ ref: ref, css: wrapperStyles }, props, { children: [_jsx(NotificationImage, { image: image }), _jsx(Headline, Object.assign({ css: cx(spacing({ top: 'giga', bottom: 'byte' }), centeredStyles), size: "two", as: headlineElement }, { children: headlineLabel })), body && _jsx(Body, Object.assign({ css: centeredStyles }, { children: body })), actions && (_jsx(ButtonGroup, { actions: actions, css: spacing({ top: 'giga' }) }))] })));
});
